<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useCatalogStore} from "@/stores/Training/Catalog";
import {computed, onMounted, ref} from "vue";
import StrSchoolAutocomplete from "@/views/Training/School/Components/StrSchoolAutocomplete.vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    name: "CatalogForm",
    components: {StrSchoolAutocomplete},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {trainingPeriods} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const catalogStore = useCatalogStore();

        const catalog = ref({
            occupationIds: [],
            schoolIds: [],
            obligatory: false,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/catalogs/${id}`).then((response) => {
                catalog.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/catalogs', catalog.value).then((response) => {
                    catalogStore.addCatalog(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/catalogs', catalog.value).then((response) => {
                    catalogStore.updateCatalog(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/catalogs/${catalog.value.id}`).then(() => {
                    catalogStore.deleteCatalog(catalog.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            catalogStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            catalog,
            trainingPeriods,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="1000"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="catalog.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('data_select.TRAINING_CATALOG_CATEGORY')" required/>
                                    <w-data-select-autocomplete
                                        v-model="catalog.categoryId"
                                        type="TRAINING_CATALOG_CATEGORY"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('common.duration')" required/>
                                    <w-text-field
                                        v-model="catalog.duration"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                        :suffix="$t('training.catalog_days')"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('training.catalog_price')" required/>
                                    <w-text-field
                                        v-model="catalog.price"
                                        type="number"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    />
                                </v-col>

                                <v-col cols="3">
                                    <v-checkbox
                                        v-model="catalog.obligatory"
                                        :label="$t('training.obligatory')"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="4" v-if="catalog.obligatory">
                                    <w-label :label="$t('training.period')" required/>
                                    <w-enum-select
                                        v-model="catalog.period"
                                        :items="trainingPeriods"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>

                                <v-col cols="12" v-if="catalog.obligatory">
                                    <w-label :label="$t('module.occupations')"/>
                                    <str-occupation-autocomplete
                                        v-model="catalog.occupationIds"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset title="Définition">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('training.catalog_objective')"/>
                                    <w-text-field
                                        v-model="catalog.objective"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('training.catalog_program')"/>
                                    <w-simple-text-area
                                        v-model="catalog.program"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('training.schools')"/>
                                    <str-school-autocomplete
                                        v-model="catalog.schoolIds"
                                        :readonly="readonly"
                                        multiple
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>